import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TemplateSelector = ({ selectedTemplate, onTemplateChange }) => {
  const templates = [
    {
      id: 'welcome-waitlist',
      name: 'Welcome to Waitlist',
      description: 'Warm welcome email for new waitlist subscribers with personalization and next steps',
      features: ['Personalized greeting', 'Family-specific messaging', 'Clear expectations', 'Social proof'],
      icon: 'Heart',
      color: 'text-coral',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200'
    },
    {
      id: 'development-update',
      name: 'Development Update',
      description: 'Regular progress updates with behind-the-scenes insights and community building',
      features: ['Progress milestones', 'Feature previews', 'Community highlights', 'Beta access info'],
      icon: 'Rocket',
      color: 'text-teal',
      bgColor: 'bg-teal-50',
      borderColor: 'border-teal-200'
    },
    {
      id: 'early-access',
      name: 'Early Access Invitation',
      description: 'Exclusive invitation for beta testing with special perks and priority access',
      features: ['Exclusive access', 'Beta perks', 'Priority support', 'Feedback collection'],
      icon: 'Star',
      color: 'text-gold',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200'
    },
    {
      id: 'launch-announcement',
      name: 'Launch Announcement',
      description: 'Official product launch notification with special pricing and celebration',
      features: ['Launch celebration', 'Special pricing', 'Getting started guide', 'Success stories'],
      icon: 'Zap',
      color: 'text-primary',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    }
  ];

  return (
    <div className="bg-white rounded-xl shadow-soft border border-border p-6">
      <div className="mb-6">
        <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
          Email Template Selection
        </h3>
        <p className="text-text-secondary">
          Choose from our collection of conversion-optimized email templates
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {templates?.map((template) => (
          <div
            key={template?.id}
            className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 hover:shadow-soft ${
              selectedTemplate === template?.id
                ? `${template?.borderColor} ${template?.bgColor} shadow-soft`
                : 'border-border bg-white hover:border-primary/20'
            }`}
            onClick={() => onTemplateChange(template?.id)}
          >
            {/* Selection Indicator */}
            {selectedTemplate === template?.id && (
              <div className="absolute top-3 right-3">
                <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                  <Icon name="Check" size={14} className="text-white" />
                </div>
              </div>
            )}

            {/* Template Header */}
            <div className="flex items-start space-x-3 mb-3">
              <div className={`p-2 rounded-lg ${template?.bgColor}`}>
                <Icon name={template?.icon} size={20} className={template?.color} />
              </div>
              <div className="flex-1">
                <h4 className="font-heading font-semibold text-foreground mb-1">
                  {template?.name}
                </h4>
                <p className="text-sm text-text-secondary leading-relaxed">
                  {template?.description}
                </p>
              </div>
            </div>

            {/* Template Features */}
            <div className="space-y-2">
              <h5 className="text-sm font-medium text-foreground">Key Features:</h5>
              <div className="grid grid-cols-2 gap-1">
                {template?.features?.map((feature, index) => (
                  <div key={index} className="flex items-center text-xs text-text-secondary">
                    <Icon name="Check" size={12} className="text-success mr-1 flex-shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Template Stats */}
            <div className="mt-4 pt-3 border-t border-border/50">
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center text-text-secondary">
                  <Icon name="Mail" size={12} className="mr-1" />
                  <span>Responsive</span>
                </div>
                <div className="flex items-center text-text-secondary">
                  <Icon name="Smartphone" size={12} className="mr-1" />
                  <span>Mobile-First</span>
                </div>
                <div className="flex items-center text-text-secondary">
                  <Icon name="Accessibility" size={12} className="mr-1" />
                  <span>WCAG AA</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Template Information */}
      <div className="mt-6 p-4 bg-surface rounded-lg border border-border">
        <h5 className="font-medium text-foreground mb-3 flex items-center">
          <Icon name="Info" size={16} className="mr-2 text-primary" />
          Template Features
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center">
            <Icon name="Palette" size={16} className="mr-2 text-secondary" />
            <span className="text-text-secondary">Brand-consistent colors</span>
          </div>
          <div className="flex items-center">
            <Icon name="Type" size={16} className="mr-2 text-accent" />
            <span className="text-text-secondary">Poppins & Inter fonts</span>
          </div>
          <div className="flex items-center">
            <Icon name="Shield" size={16} className="mr-2 text-gold" />
            <span className="text-text-secondary">Privacy-compliant</span>
          </div>
          <div className="flex items-center">
            <Icon name="Moon" size={16} className="mr-2 text-primary" />
            <span className="text-text-secondary">Dark mode support</span>
          </div>
          <div className="flex items-center">
            <Icon name="Globe" size={16} className="mr-2 text-secondary" />
            <span className="text-text-secondary">Cross-client compatible</span>
          </div>
          <div className="flex items-center">
            <Icon name="BarChart" size={16} className="mr-2 text-accent" />
            <span className="text-text-secondary">Conversion optimized</span>
          </div>
        </div>
      </div>
      {/* Quick Actions */}
      <div className="mt-6 flex items-center justify-between">
        <div className="text-sm text-text-secondary">
          Selected: <span className="font-medium text-foreground">
            {templates?.find(t => t?.id === selectedTemplate)?.name || 'None'}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
            iconSize={16}
          >
            Export Template
          </Button>
          <Button
            variant="secondary"
            size="sm"
            iconName="Eye"
            iconPosition="left"
            iconSize={16}
          >
            Live Preview
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TemplateSelector;